import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Megaphone, Users, MessageSquare, TrendingUp } from 'lucide-react';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface CampaignRow {
  id: string;
  campaign_slug: string;
  campaign_name: string;
  brand_name: string | null;
  active: boolean;
  status: string | null;
  tenant_id: string;
  bot_id: string | null;
  created_at: string;
  updated_at: string;
  notification_email: string | null;
}

interface CampaignStats {
  campaign_slug: string;
  lead_count: number;
  hot_count: number;
  quote_count: number;
}

const AdminCampaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [stats, setStats] = useState<Record<string, CampaignStats>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data: camps } = await supabase
        .from('campaign_configs')
        .select('id, campaign_slug, campaign_name, brand_name, active, status, tenant_id, bot_id, created_at, updated_at, notification_email')
        .order('created_at', { ascending: false });

      if (camps) {
        setCampaigns(camps as unknown as CampaignRow[]);

        // Get lead stats per campaign
        const slugs = camps.map((c: any) => c.campaign_slug);
        const { data: leads } = await supabase
          .from('campaign_leads')
          .select('campaign, lead_temperature, quote_given')
          .in('campaign', slugs);

        if (leads) {
          const statsMap: Record<string, CampaignStats> = {};
          for (const lead of leads as any[]) {
            if (!statsMap[lead.campaign]) {
              statsMap[lead.campaign] = { campaign_slug: lead.campaign, lead_count: 0, hot_count: 0, quote_count: 0 };
            }
            statsMap[lead.campaign].lead_count++;
            if (lead.lead_temperature === 'hot') statsMap[lead.campaign].hot_count++;
            if (lead.quote_given) statsMap[lead.campaign].quote_count++;
          }
          setStats(statsMap);
        }
      }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return <AdminLayout><div className="flex justify-center py-20"><LoadingSpinner /></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Campaigns</h1>
            <p className="text-muted-foreground text-sm mt-1">Campaign bots, leads, and performance</p>
          </div>
          <Badge variant="outline" className="text-sm">{campaigns.length} campaigns</Badge>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><Megaphone className="h-4 w-4" /> Active</CardTitle></CardHeader>
            <CardContent><p className="text-2xl font-bold">{campaigns.filter(c => c.active).length}</p></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><Users className="h-4 w-4" /> Total Leads</CardTitle></CardHeader>
            <CardContent><p className="text-2xl font-bold">{Object.values(stats).reduce((a, s) => a + s.lead_count, 0)}</p></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Hot Leads</CardTitle></CardHeader>
            <CardContent><p className="text-2xl font-bold text-orange-500">{Object.values(stats).reduce((a, s) => a + s.hot_count, 0)}</p></CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Quotes Given</CardTitle></CardHeader>
            <CardContent><p className="text-2xl font-bold">{Object.values(stats).reduce((a, s) => a + s.quote_count, 0)}</p></CardContent>
          </Card>
        </div>

        {/* Campaign list */}
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Campaign</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Brand</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground">Leads</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground hidden sm:table-cell">Hot</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground hidden sm:table-cell">Quotes</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(c => {
                const s = stats[c.campaign_slug] || { lead_count: 0, hot_count: 0, quote_count: 0 };
                return (
                  <tr key={c.id} className="border-b hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <Link to={`/admin/campaigns/${c.id}`} className="font-medium text-foreground hover:text-primary">
                        {c.campaign_name}
                      </Link>
                      <p className="text-xs text-muted-foreground mt-0.5">{c.campaign_slug}</p>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{c.brand_name || '—'}</td>
                    <td className="px-4 py-3">
                      <Badge variant={c.active ? 'default' : 'secondary'} className="text-xs">
                        {c.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{s.lead_count}</td>
                    <td className="px-4 py-3 text-right hidden sm:table-cell">
                      {s.hot_count > 0 ? <span className="text-orange-500 font-medium">{s.hot_count}</span> : '—'}
                    </td>
                    <td className="px-4 py-3 text-right hidden sm:table-cell">{s.quote_count || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCampaigns;
